import { useCallback, useEffect, useRef, useState } from 'react';

/** Quick presets (same as original sleep timer). */
export const AUDIO_SLEEP_TIMER_PRESETS = [
  { value: '5', label: '5 minutes' },
  { value: '15', label: '15 minutes' },
  { value: '30', label: '30 minutes' },
  { value: '45', label: '45 minutes' },
  { value: '60', label: '1 hour' },
  { value: '90', label: '1½ hours' },
];

export const AUDIO_SLEEP_TIMER_MODES = [
  { value: 'off', label: 'Off' },
  ...AUDIO_SLEEP_TIMER_PRESETS,
  { value: 'custom', label: 'Custom…' },
  { value: 'end-track', label: 'End of song' },
  { value: 'end-queue', label: 'End of queue' },
];

export const AUDIO_SLEEP_HOUR_OPTIONS = [
  { value: '0', label: '0 hr' },
  { value: '1', label: '1 hr' },
  { value: '2', label: '2 hr' },
  { value: '3', label: '3 hr' },
];

export const AUDIO_SLEEP_MINUTE_OPTIONS = [
  { value: '0', label: '00 min' },
  { value: '5', label: '5 min' },
  { value: '10', label: '10 min' },
  { value: '15', label: '15 min' },
  { value: '30', label: '30 min' },
  { value: '45', label: '45 min' },
  { value: '60', label: '60 min' },
];

const PRESET_DURATION = {
  '5': { hours: 0, minutes: 5 },
  '15': { hours: 0, minutes: 15 },
  '30': { hours: 0, minutes: 30 },
  '45': { hours: 0, minutes: 45 },
  '60': { hours: 1, minutes: 0 },
  '90': { hours: 1, minutes: 30 },
};

export function formatSleepTimerRemaining(totalSeconds) {
  if (totalSeconds == null || totalSeconds < 0) return '';
  const sec = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Stops playback after a custom duration or when a song / queue ends.
 */
export function useAudioSleepTimer(onSleep) {
  const [timerMode, setTimerMode] = useState('off');
  const [endsAtMs, setEndsAtMs] = useState(null);
  const [remainingSec, setRemainingSec] = useState(null);
  const [durationMenuValue, setDurationMenuValue] = useState('off');
  const endBehaviorRef = useRef(null);
  const onSleepRef = useRef(onSleep);
  onSleepRef.current = onSleep;

  const clearTimer = useCallback(() => {
    setTimerMode('off');
    setEndsAtMs(null);
    setRemainingSec(null);
    setDurationMenuValue('off');
    endBehaviorRef.current = null;
  }, []);

  const startDurationTimer = useCallback(
    (hours, minutes, menuValue = 'custom') => {
      const h = Math.max(0, Math.min(23, Number(hours) || 0));
      const m = Math.max(0, Math.min(59, Number(minutes) || 0));
      const totalSec = h * 3600 + m * 60;
      if (totalSec <= 0) {
        clearTimer();
        return false;
      }
      endBehaviorRef.current = null;
      setTimerMode('duration');
      setDurationMenuValue(menuValue);
      setEndsAtMs(Date.now() + totalSec * 1000);
      return true;
    },
    [clearTimer]
  );

  const setSleepTimerMode = useCallback(
    (mode) => {
      if (!mode || mode === 'off') {
        clearTimer();
        return;
      }
      if (mode === 'custom') {
        setTimerMode('custom');
        setEndsAtMs(null);
        setRemainingSec(null);
        setDurationMenuValue('custom');
        endBehaviorRef.current = null;
        return;
      }
      if (PRESET_DURATION[mode]) {
        const { hours, minutes } = PRESET_DURATION[mode];
        startDurationTimer(hours, minutes, mode);
        return;
      }
      if (mode === 'end-track') {
        setTimerMode('end-track');
        setEndsAtMs(null);
        setRemainingSec(null);
        setDurationMenuValue('end-track');
        endBehaviorRef.current = 'track';
        return;
      }
      if (mode === 'end-queue') {
        setTimerMode('end-queue');
        setEndsAtMs(null);
        setRemainingSec(null);
        setDurationMenuValue('end-queue');
        endBehaviorRef.current = 'queue';
        return;
      }
    },
    [clearTimer, startDurationTimer]
  );

  useEffect(() => {
    if (!endsAtMs) {
      if (timerMode === 'duration') setRemainingSec(null);
      return undefined;
    }
    const tick = () => {
      const left = Math.max(0, Math.ceil((endsAtMs - Date.now()) / 1000));
      setRemainingSec(left);
      if (left <= 0) {
        clearTimer();
        onSleepRef.current?.();
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [endsAtMs, clearTimer, timerMode]);

  /** @returns {'handled' | 'advance' | null} */
  const handleTrackEnded = useCallback(
    (hasNextInQueue) => {
      const behavior = endBehaviorRef.current;
      if (behavior === 'track') {
        clearTimer();
        onSleepRef.current?.();
        return 'handled';
      }
      if (behavior === 'queue') {
        if (hasNextInQueue) return 'advance';
        clearTimer();
        onSleepRef.current?.();
        return 'handled';
      }
      return null;
    },
    [clearTimer]
  );

  const remainingLabel =
    timerMode === 'end-track'
      ? 'End of song'
      : timerMode === 'end-queue'
        ? 'End of queue'
        : remainingSec != null
          ? formatSleepTimerRemaining(remainingSec)
          : '';

  const isActive = timerMode === 'duration' || timerMode === 'end-track' || timerMode === 'end-queue';

  const dropdownValue = durationMenuValue;

  return {
    timerMode,
    dropdownValue,
    setSleepTimerMode,
    startDurationTimer,
    clearTimer,
    remainingSec,
    remainingLabel,
    isActive,
    handleTrackEnded,
  };
}
